"use client";

import React, { useEffect, createContext, useState, useContext } from "react";
import {
  init,
  add,
  logEvent,
  Identify,
  identify,
} from "@amplitude/analytics-browser";
import { Session } from "next-auth";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { useSearchParams } from "next/navigation";
import { Experiment, ExperimentClient } from "@amplitude/experiment-js-client";

const AMPL_API_KEY = "6efad090998a276eb01a6024ee2f85a5";

export interface AmplitudeContextState {
  experiment?: ExperimentClient;
}

export const AmplitudeContext = createContext<AmplitudeContextState | null>(
  null,
);

export const useAmplitudeContext = (): AmplitudeContextState => {
  const context = useContext(AmplitudeContext);
  if (!context) {
    throw new Error("useAmplitude must be used within a AmplitudeContext");
  }
  return context;
};

export interface AmplitudeProviderProps {
  children: React.ReactNode;
  session?: Session | null;
}

function AmplitudeContextProvider(props: AmplitudeProviderProps) {
  // setting up amplitude session
  const searchParams = useSearchParams();
  const userSource = searchParams.get("source");
  const sourceVoice = searchParams.get("Voice");

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      add(
        sessionReplayPlugin({
          sampleRate: 1,
        }),
      );

      if (props.session?.user != null) {
        const identifyEvent = new Identify();
        identifyEvent.set("email", props.session.user.email!);
        identifyEvent.set("userName", props.session.user.name!);
        identifyEvent.set("userRole", props.session.user.role!);
        identify(identifyEvent);
      }

      init(AMPL_API_KEY, props.session?.user.id ?? undefined, {
        defaultTracking: {
          sessions: true,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (userSource != null && process.env.NODE_ENV === "production") {
      logEvent("user-source", { source: userSource, voice: sourceVoice });
    }
  });

  const [experiment, setExperiment] = useState<ExperimentClient>();

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      const experiment =
        Experiment.initializeWithAmplitudeAnalytics(AMPL_API_KEY);
      setExperiment(experiment);
    }
  }, [props.session?.user]);

  return (
    <AmplitudeContext.Provider
      value={{
        experiment,
      }}
    >
      {props.children}
    </AmplitudeContext.Provider>
  );
}

export default AmplitudeContextProvider;
